import { useInfiniteQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight } from "lucide-react";
import React, { useState } from "react";

import * as ozon from "@/api/ozonSupply";
import { Button } from "@/components/ui/button";
import { extractErrorMessage } from "@/lib/api-errors";
import { cn } from "@/lib/utils";
import { int0 } from "../format";
import { slotWatchEventsKey } from "./ozonQueries";
import { fmtEventAt, isErrorEvent, stepLabel } from "./slotWatch";
import { WATCH_POLL_MS } from "./useSlotWatch";

/** Сколько событий за запрос: тик пишет 2–5 событий, сотня — это примерно полчаса ночи. */
export const EVENTS_PAGE = 100;

/** Журнал наблюдателя: по нему восстанавливают ночь — что спросили у площадки, что она ответила
 *  и что из этого решили. Свёрнут по умолчанию: журнал нужен разбору, а не каждому заходу.
 *
 *  ⚠️ «Только ошибки» фильтрует ЗАГРУЖЕННОЕ: сервер фильтра не знает, и экран так и пишет, сколько
 *  событий просмотрено, — иначе «ошибок нет» читалось бы как «ошибок не было за всю ночь».
 */
export function SlotWatchJournal({
  watchId,
  live,
}: {
  watchId: number;
  /** мониторинг активен — перечитываем журнал вместе с ним */
  live: boolean;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [onlyErrors, setOnlyErrors] = useState(false);
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(new Set());
  const q = useInfiniteQuery({
    queryKey: slotWatchEventsKey(watchId),
    queryFn: ({ pageParam }) => ozon.getSlotWatchEvents(watchId, EVENTS_PAGE, pageParam),
    initialPageParam: null as number | null,
    // страница короче полной — дальше событий нет
    getNextPageParam: (last) =>
      last.length < EVENTS_PAGE ? undefined : last[last.length - 1]!.id,
    enabled: open,
    refetchInterval: live && open ? WATCH_POLL_MS : false,
  });

  const all = q.data?.pages.flat() ?? [];
  const shown = onlyErrors ? all.filter(isErrorEvent) : all;
  const toggle = (id: number): void =>
    setExpanded((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const td = "px-2 py-1 align-top";

  return (
    <div className="space-y-2">
      <button
        type="button"
        aria-expanded={open}
        className="flex items-center gap-1 text-sm font-medium hover:underline"
        onClick={() => setOpen(!open)}
      >
        {open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
        Журнал наблюдателя
      </button>
      {open && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <label className="flex items-center gap-1">
              <input
                type="checkbox"
                checked={onlyErrors}
                onChange={(e) => setOnlyErrors(e.target.checked)}
              />
              только ошибки
            </label>
            {q.data && (
              <span>
                загружено событий: {int0(all.length)}
                {onlyErrors && ` · ошибок среди них: ${int0(shown.length)}`}
              </span>
            )}
          </div>
          {q.isError && (
            <p className="text-xs text-destructive">
              ⚠ {extractErrorMessage(q.error, "Журнал не прочитался")}
            </p>
          )}
          {q.isLoading && <p className="text-xs text-muted-foreground">Загрузка…</p>}
          {q.data && all.length === 0 && (
            <p className="text-xs text-muted-foreground">Событий пока нет — наблюдатель ещё не тикал.</p>
          )}
          {q.data && all.length > 0 && shown.length === 0 && (
            <p className="text-xs text-muted-foreground">
              Среди загруженных событий ошибок нет
              {q.hasNextPage ? " — «Показать ещё», чтобы смотреть раньше." : "."}
            </p>
          )}
          {shown.length > 0 && (
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full min-w-[900px] border-collapse text-xs">
                <thead className="bg-muted/40 text-left text-muted-foreground">
                  <tr>
                    <th className="px-2 py-1 font-normal">Время</th>
                    <th className="px-2 py-1 font-normal">Тик</th>
                    <th className="px-2 py-1 font-normal">Шаг</th>
                    <th className="px-2 py-1 font-normal">Итог</th>
                    <th className="px-2 py-1 font-normal">Метод Ozon</th>
                    <th className="px-2 py-1 text-right font-normal">HTTP</th>
                    <th className="px-2 py-1 text-right font-normal">мс</th>
                    <th className="px-2 py-1 font-normal">Сообщение</th>
                    <th className="px-2 py-1 font-normal" />
                  </tr>
                </thead>
                <tbody>
                  {shown.map((e) => {
                    const bad = isErrorEvent(e);
                    const isOpen = expanded.has(e.id);
                    return (
                      <React.Fragment key={e.id}>
                        <tr
                          data-testid="watch-event"
                          className={cn("border-t", bad && "bg-red-50/60 dark:bg-red-950/20")}
                        >
                          <td className={cn(td, "whitespace-nowrap tabular-nums")}>{fmtEventAt(e.at)}</td>
                          <td
                            className={cn(td, "whitespace-nowrap font-mono text-muted-foreground")}
                            title={e.tick_id}
                          >
                            …{e.tick_id.slice(-6)}
                          </td>
                          <td className={cn(td, "whitespace-nowrap")}>{stepLabel(e.step)}</td>
                          <td className={td}>
                            {bad ? (
                              <span className="rounded bg-red-100 px-1.5 py-0.5 text-red-700 dark:bg-red-950 dark:text-red-300">
                                ошибка
                              </span>
                            ) : (
                              <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
                                ок
                              </span>
                            )}
                          </td>
                          <td className={cn(td, "font-mono")}>{e.ozon_path ?? "—"}</td>
                          <td className={cn(td, "text-right tabular-nums")}>{e.http_status ?? "—"}</td>
                          <td className={cn(td, "text-right tabular-nums")}>
                            {e.duration_ms != null ? int0(e.duration_ms) : "—"}
                          </td>
                          <td className={cn(td, "whitespace-pre-wrap")}>{e.message}</td>
                          <td className={cn(td, "text-right")}>
                            {(e.request != null || e.response != null) && (
                              <button
                                type="button"
                                aria-expanded={isOpen}
                                className="rounded px-1.5 py-0.5 font-mono text-muted-foreground hover:bg-accent hover:text-foreground"
                                onClick={() => toggle(e.id)}
                              >
                                {isOpen ? "скрыть" : "JSON"}
                              </button>
                            )}
                          </td>
                        </tr>
                        {isOpen && (
                          <tr className="border-t bg-muted/30">
                            <td colSpan={9} className="space-y-2 px-2 py-2">
                              <JsonBlock title="Запрос" value={e.request} />
                              <JsonBlock title="Ответ" value={e.response} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {q.isFetchNextPageError && (
            <p className="text-xs text-destructive">
              ⚠ {extractErrorMessage(q.error, "Следующая страница журнала не прочиталась")}
            </p>
          )}
          {q.hasNextPage && (
            <Button
              size="sm"
              variant="outline"
              disabled={q.isFetchingNextPage}
              onClick={() => void q.fetchNextPage()}
            >
              {q.isFetchingNextPage ? "Загрузка…" : "Показать ещё"}
            </Button>
          )}
          {q.data && all.length > 0 && !q.hasNextPage && (
            <p className="text-xs text-muted-foreground">Это все события мониторинга.</p>
          )}
        </div>
      )}
    </div>
  );
}

/** JSON запроса или ответа как есть; строку (срез большого ответа) — текстом. */
function JsonBlock({ title, value }: { title: string; value: unknown }): React.ReactElement {
  const text =
    value == null ? "—" : JSON.stringify(value, null, 2);
  return (
    <div>
      <div className="mb-0.5 text-[11px] uppercase tracking-wide text-muted-foreground">{title}</div>
      <pre className="max-h-80 overflow-auto rounded border bg-background p-2 font-mono text-[11px] leading-snug">
        {text}
      </pre>
    </div>
  );
}
